import {
  Autocomplete,
  createFilterOptions,
  debounce,
  Grid,
  TextField,
  Typography,
} from "@mui/material";
import FormControl from "@mui/material/FormControl";
import { isFulfilled } from "@reduxjs/toolkit";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAppDispatch } from "../../../app/hooks";
import { CenteredGridItem } from "../../../common/components/functional/FilterSearchField/styled";
import { EezyCompany, EezyCostCenter } from "../../invoices/invoiceAPI";
import { fetchEezyCompanies } from "../../invoices/invoiceSlice";
import { Controller, useFormContext } from "react-hook-form";
import { DesktopDatePicker } from "@mui/x-date-pickers";
import { customerOrgAndCompAutocomplete } from "../../customer/customerAPI";
import { CustomerOrgCompany } from "../../../common/types/apiTypes";
import { CustomerOrg } from "../../entries/types";

export const EmployerHealthInsuranceReductionFilters = () => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const { control } = useFormContext();

  const [eezyCompanyOptions, setEezyCompanyOptions] = useState<EezyCompany[]>(
    [],
  );
  const [costCenterOptions, setCostCenterOptions] = useState<EezyCostCenter[]>(
    [],
  );
  const [customerOrgOptions, setCustomerOrgOptions] = useState<any[]>([]);
  const [customerSearchString, setCustomerSearchString] = useState<string>("");

  useEffect(() => {
    dispatch(fetchEezyCompanies({})).then((action) => {
      if (isFulfilled(action)) {
        setEezyCompanyOptions(action.payload);
        setCostCenterOptions(
          action.payload
            .map((company) => company.costCenters)
            .flat()
            .filter(Boolean),
        );
      }
    });
  }, [dispatch]);

  const dispatchCustomerOrgSearch = (searchString: string) => {
    try {
      customerOrgAndCompAutocomplete({ name: searchString }).then((res) => {
        const customerCompanies: CustomerOrgCompany[] = res;
        const customerOrgs: CustomerOrg[] = [];
        customerCompanies.forEach((company) => {
          company.customerOrganizations.forEach((org) => {
            customerOrgs.push(org);
          });
        });
        setCustomerOrgOptions(customerOrgs);
      });
    } catch (e: any) {
      console.error("Failed to fetch CustomerOrganizations: ", e);
    }
  };

  const handleCustomerSearchStringChange = debounce(
    (value: string) => setCustomerSearchString(value),
    500,
  );

  useEffect(() => {
    if (customerSearchString !== "") {
      dispatchCustomerOrgSearch(customerSearchString);
    }
  }, [customerSearchString]);

  return (
    <>
      <CenteredGridItem item container spacing={2} xs={12}>
        <Grid item xs={4}>
          <Typography align="right">{t("employer")}</Typography>
        </Grid>
        <Grid item xs={8}>
          <Controller
            name="employer"
            control={control}
            render={({ field: { value, onChange } }) => (
              <FormControl fullWidth size="small">
                <Autocomplete
                  value={value}
                  sx={{
                    backgroundColor: "rgba(249, 249, 250, 0.6)",
                  }}
                  getOptionLabel={(option) => option.name}
                  options={eezyCompanyOptions}
                  filterOptions={createFilterOptions({
                    stringify: (option) => JSON.stringify(option),
                  })}
                  renderInput={(params) => (
                    <TextField {...params} size="small" name="employer" />
                  )}
                  onChange={(_, newValue: EezyCompany) => {
                    onChange(newValue);
                  }}
                />
              </FormControl>
            )}
          />
        </Grid>
      </CenteredGridItem>
      <CenteredGridItem item container spacing={2} xs={12}>
        <Grid item xs={4}>
          <Typography align="right">
            {t("payslips:employerCostCenter")}
          </Typography>
        </Grid>
        <Grid item xs={8}>
          <Controller
            name="employerCostCenter"
            control={control}
            render={({ field: { value, onChange } }) => (
              <FormControl fullWidth size="small">
                <Autocomplete
                  value={value}
                  sx={{
                    backgroundColor: "rgba(249, 249, 250, 0.6)",
                  }}
                  getOptionLabel={(option) => option.name}
                  options={costCenterOptions}
                  filterOptions={createFilterOptions({
                    stringify: (option) => JSON.stringify(option),
                  })}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      size="small"
                      name="employerCostCenter"
                    />
                  )}
                  onChange={(_, newValue: EezyCompany) => {
                    onChange(newValue);
                  }}
                />
              </FormControl>
            )}
          />
        </Grid>
      </CenteredGridItem>
      <CenteredGridItem item container spacing={2} xs={12}>
        <Grid item xs={4}>
          <Typography align="right">{t("customerOrganization")}</Typography>
        </Grid>
        <Grid item xs={8}>
          <Controller
            name="customerOrganization"
            control={control}
            render={({ field: { value, onChange } }) => (
              <FormControl fullWidth size="small">
                <Autocomplete
                  value={value}
                  size="small"
                  options={customerOrgOptions}
                  noOptionsText={t("common:writeToSearch")}
                  getOptionLabel={(option) => option?.name || ""}
                  onChange={(_: any, newValue: any) => onChange(newValue)}
                  onInputChange={(_, newValue: string) => {
                    if (!!newValue) {
                      handleCustomerSearchStringChange(newValue);
                    }
                  }}
                  renderInput={(params: any) => (
                    <TextField name="customerOrganization" {...params} />
                  )}
                />
              </FormControl>
            )}
          />
        </Grid>
      </CenteredGridItem>
      <CenteredGridItem item container spacing={2} xs={12}>
        <Grid item xs={4}>
          <Typography align="right">{t("payslips:monthYear")}</Typography>
        </Grid>
        <Grid item xs={8}>
          <Controller
            control={control}
            name="yearMonth"
            rules={{ required: true }}
            render={({ field: { value, onChange } }) => (
              <FormControl fullWidth>
                <DesktopDatePicker
                  name="yearMonth"
                  value={value}
                  onChange={onChange}
                  views={["month", "year"]}
                  format="MM/yy"
                  slotProps={{
                    textField: {
                      size: "small",
                      fullWidth: true,
                    },
                  }}
                />
              </FormControl>
            )}
          />
        </Grid>
      </CenteredGridItem>
    </>
  );
};
